import {
  Form,
  Button,
  Flex,
  Drawer,
  Typography,
  Spin,
  Input,
} from 'antd';
import InputFormBox from 'components/bank/AGBBank/InputFormBox';
import NoComponent from 'components/bank/AGBBank/NoComponent';
import FormItemInput from 'components/shared/FormItemInput';
import TransferConfirmation from 'components/shared/TransferConfirmation';
import { filteredFormInputItems } from 'constants/InputItems/agbInputItems';
import { useMemo } from 'react';
import { ITransferForm } from 'interfaces/components/pageProps';
import { IConfirmationData } from 'interfaces/queries/transactions';
import AccountDropdown from 'components/shared/AccountDropdown';
import ContactDropdown from 'components/shared/ContactDropdown';
import OssixProvidersDropdown from 'components/shared/OssixProvidersDropdown';
import { ActionType } from 'types/generalType';
import { TargetType } from 'types/generalType';
import DynamicForm from 'components/ossix/DynamicForm';
import { useLanguage } from 'hooks/Language/useLanguage';
import { DrawerStyles } from 'antd/es/drawer/DrawerPanel';
import agbColors from 'constants/Colors/agbColors';
import { CloseOutlined } from '@ant-design/icons';
import { X } from 'lucide-react';

const { Text } = Typography;

const LocalTransfer: React.FC<ITransferForm> = ({
  selectAccountNumber,
  form,
  step,
  onPrevious,
  accountNumber,
  setAccountNumber,
  loadingAccount,
  recipientAccountNumber,
  onHandleDisplaySelectContact,
  displaySelectContact,
  isLoadingContacts,
  contactAccount,
  setContactAccount,
  onHandleCloseSelectContact,
  setSelectedProvider,
  dynamicFormItem,
  handleCloseOpenDrawer,
  isDrawerOpen,
}) => {
  const { translate } = useLanguage();

  const {
    state,
    fields,
    meta,
    handleInputChange,
    loadVerifyAccount,
    onHandleVerifyAccount,
    filterCountry,
    isVerifyAccountSuccess,
    contextHolder,
    onHandleValueChange,
    onHandleTransferConfirmation,
    loadTransferConfirmation,
    transferConfirmationData,
    onPINValuesChange,
    onHandleConfirmTransfer,
    loadSendFundsToGateway,
    userData,
  } = dynamicFormItem;

  const formItems = useMemo(
    () =>
      filteredFormInputItems(['amount']).map((item, index) => (
        <FormItemInput
          key={index}
          label={item.label}
          placeholder={item.placeholder}
          name={item.name}
          disabled={item.disabled}
          className="amount-input"
        />
      )),
    [],
  );

  const convertConfirmationData = (data: IConfirmationData) => {
    return [
      { label: 'Amount to be sent', value: data?.Amount },
      { label: 'Fees', value: data?.Fees },
      { label: 'Exchange fees', value: data?.ExchangeFees },
      { label: 'Exchange rate', value: data?.ExchangeRate },
      { label: 'Taxes', value: data?.Taxes },
      { label: 'Total amount', value: data?.TotalAmount },
    ];
  };

  const confirmationItems = convertConfirmationData(
    transferConfirmationData,
  );

  const drawerStyles: DrawerStyles = {
    header: {
      background: agbColors.navyBlueColor,
      color: agbColors.lightBlueColor,
    },
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="drawer-scrollable-content">
            <InputFormBox
              title={state?.chosenOption}
              onSelectRecipient={onHandleDisplaySelectContact}
              select={[
                <AccountDropdown
                  account={accountNumber}
                  loading={loadingAccount}
                  options={selectAccountNumber}
                  setAccount={setAccountNumber}
                  size="100%"
                />,
              ]}
              formItems={[
                <Form
                  form={form}
                  layout="vertical"
                  key="form"
                  onValuesChange={onHandleValueChange}
                  className="transfer-form"
                >
                  <Form.Item
                    label={translate('Select operator')}
                    className="small-margin-bottom"
                  >
                    <OssixProvidersDropdown
                      style={{ width: '100%' }}
                      getSelectedProvider={setSelectedProvider}
                      payload={{
                        ActionType: ActionType.Transfer,
                        CountryCode:
                          userData?.OperationCountry ||
                          userData?.[0]?.OperationCountry,
                        TargetType: TargetType.Bank,
                      }}
                      defaultValue={state?.OperatorID || undefined}
                      showDefaultValue
                    />
                  </Form.Item>
                  <div className="dynamic-form-container">
                    <DynamicForm
                      meta={meta}
                      inputs={fields}
                      amountRequired={false}
                      verifyAccount={meta?.verifyAccount}
                      handleInputChange={handleInputChange}
                      countries={filterCountry}
                      defaultCountry={
                        userData?.OperationCountry ||
                        userData?.[0]?.OperationCountry
                      }
                    />
                  </div>
                  {displaySelectContact && (
                    <div className="beneficiary-selection-container">
                      <Flex
                        align="center"
                        gap="small"
                        style={{ width: '100%' }}
                      >
                        <Form.Item
                          label={translate('Select a recipient')}
                          className="small-margin-bottom"
                          name="recipient"
                          style={{ width: '100%' }}
                        >
                          <ContactDropdown
                            contact={contactAccount}
                            setContact={setContactAccount}
                            loading={isLoadingContacts}
                            options={recipientAccountNumber}
                          />
                        </Form.Item>
                        <Form.Item
                          label=" "
                          className="small-margin-bottom"
                        >
                          <Button
                            size="large"
                            type="primary"
                            ghost
                            onClick={onHandleCloseSelectContact}
                            icon={
                              <X
                                strokeWidth={1}
                                color="red"
                                size={20}
                              />
                            }
                          />
                        </Form.Item>
                      </Flex>
                    </div>
                  )}
                  {formItems}
                </Form>,
              ]}
            />
          </div>
        );
      case 2:
        return (
          <TransferConfirmation
            isLoading={loadSendFundsToGateway}
            items={confirmationItems}
            onHandleBack={onPrevious}
            onHandleConfirm={onHandleConfirmTransfer}
            onValuesChange={onPINValuesChange}
            hideButtons={true}
          />
        );
      default:
        return <NoComponent />;
    }
  };

  const renderStep1Actions = () => (
    <div className="drawer-footer">
      {!isVerifyAccountSuccess && meta?.verifyAccount ? (
        <Button
          type="primary"
          onClick={onHandleVerifyAccount}
          loading={loadVerifyAccount}
          size="large"
          block
        >
          {translate('Next')}
        </Button>
      ) : (
        <Button
          ghost
          type="primary"
          onClick={onHandleTransferConfirmation}
          loading={loadTransferConfirmation}
          size="large"
          block
        >
          {translate('Proceed')}
        </Button>
      )}
    </div>
  );

  const renderFooter = () => {
    switch (step) {
      case 1:
        return renderStep1Actions();
      case 2:
        return (
          <div className="drawer-footer dual-button">
            <Button
              variant="filled"
              color="volcano"
              type="primary"
              onClick={onPrevious}
              size="large"
            >
              {translate('Back')}
            </Button>
            <Button
              type="primary"
              onClick={onHandleConfirmTransfer}
              loading={loadSendFundsToGateway}
              size="large"
            >
              {translate('Confirm')}
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {contextHolder}
      <Drawer
        width={500}
        open={isDrawerOpen?.open}
        title={
          <Text
            style={{ color: 'white', fontSize: 16, fontWeight: 500 }}
          >
            {translate(isDrawerOpen?.drawerTitle)}
          </Text>
        }
        styles={drawerStyles}
        closeIcon={false}
        onClose={handleCloseOpenDrawer}
        className="transfer-mno-drawer"
        rootClassName="transfer-mno-drawer-root"
        extra={
          <Button
            type="text"
            onClick={handleCloseOpenDrawer}
            icon={<CloseOutlined style={{ color: 'white' }} />}
            className="drawer-close-button"
          />
        }
      >
        {isDrawerOpen?.open ? (
          <div className="drawer-layout-container">
            {renderStep()}
            {renderFooter()}
          </div>
        ) : (
          <Flex
            justify="center"
            align="center"
            style={{ height: '100%' }}
          >
            <Spin size="large" />
          </Flex>
        )}
      </Drawer>
    </>
  );
};

export default LocalTransfer;
